export type LatLngLiteral = { lat: number; lng: number };

export type PinAddress = {
  address1: string;
  city: string;
  state: string;
  zipCode: string;
  county: string;
};

export const GEOFENCE_MILES = 0.5;
export const GEOFENCE_METERS = GEOFENCE_MILES * 1609.344;
export const GEOFENCE_MESSAGE =
  'The pin cannot be moved more than 0.5 miles from the original location.';

export const toNumber = (value: unknown): number | null => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

export const toLatLng = (
  latitude: unknown,
  longitude: unknown
): LatLngLiteral | null => {
  const lat = toNumber(latitude);
  const lng = toNumber(longitude);
  if (lat === null || lng === null) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
};

export const roundCoordinate = (value: number) => Number(value.toFixed(6));

export const distanceMeters = (a: LatLngLiteral, b: LatLngLiteral): number =>
  google.maps.geometry.spherical.computeDistanceBetween(
    new google.maps.LatLng(a.lat, a.lng),
    new google.maps.LatLng(b.lat, b.lng)
  );

export const isOutsideGeofence = (
  position: LatLngLiteral,
  anchor: LatLngLiteral
): boolean => distanceMeters(position, anchor) > GEOFENCE_METERS;

let geocoder: google.maps.Geocoder | null = null;
const getGeocoder = () => {
  if (!geocoder) geocoder = new google.maps.Geocoder();
  return geocoder;
};

const findComponent = (
  components: google.maps.GeocoderAddressComponent[],
  type: string
) => components.find((item) => item.types.includes(type));

export const geocodeAddress = async ({
  address1,
  city,
  state,
  zipCode,
}: {
  address1: string;
  city: string;
  state: string;
  zipCode: string;
}): Promise<LatLngLiteral | null> => {
  try {
    const { results } = await getGeocoder().geocode({
      address: `${address1}, ${city}, ${state} ${zipCode}`,
      region: 'us',
    });
    const location = results?.[0]?.geometry?.location;
    if (!location) return null;
    return {
      lat: roundCoordinate(location.lat()),
      lng: roundCoordinate(location.lng()),
    };
  } catch {
    return null;
  }
};

export const reverseGeocode = async (
  position: LatLngLiteral
): Promise<PinAddress | null> => {
  try {
    const { results } = await getGeocoder().geocode({ location: position });
    const components = results?.[0]?.address_components;
    if (!components) return null;

    const streetNumber = findComponent(components, 'street_number')?.long_name;
    const route = findComponent(components, 'route')?.long_name;
    const address1 = [streetNumber, route].filter(Boolean).join(' ');
    const city =
      findComponent(components, 'locality')?.long_name ??
      findComponent(components, 'sublocality')?.long_name ??
      findComponent(components, 'postal_town')?.long_name ??
      '';

    return {
      address1: address1 || results[0].formatted_address.split(',')[0] || '',
      city,
      state:
        findComponent(components, 'administrative_area_level_1')?.short_name ??
        '',
      zipCode: findComponent(components, 'postal_code')?.long_name ?? '',
      county:
        findComponent(components, 'administrative_area_level_2')?.long_name ??
        '',
    };
  } catch {
    return null;
  }
};
